import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'

interface AcquisitionCheckboxProps {
  checked: boolean
  onChange: () => void
  label: string
  /** Render the locked-price annotation (materials only — tools are reusable, no per-row price). */
  lockedPriceAnnotation?: string | null
  /**
   * Tools acquired via Inventory show "in your Inventory" instead of "for this project". The
   * checkbox is rendered uncheckable (Inventory is the broader signal; UI just shows the badge).
   */
  inventoryBadge?: boolean
  /** Disable the toggle while a mutation is in flight. */
  pending?: boolean
}

/**
 * Phase 12: tap-target checkbox for marking a StepMaterial or StepTool acquired. Mobile-first
 * (44×44 effective hit target via the wrapping label's padding). Long-press menu (edit locked
 * price / add to Inventory) is a Phase 12.5 polish slice — for now, every UI control is a
 * single tap; price-edit goes through the existing PATCH endpoint via a separate flow.
 */
export const AcquisitionCheckbox = ({
  checked,
  onChange,
  label,
  lockedPriceAnnotation,
  inventoryBadge,
  pending,
}: AcquisitionCheckboxProps) => (
  <label
    className={cn(
      'inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs transition-colors',
      'hover:bg-accent active:bg-accent',
      checked && 'text-muted-foreground line-through',
      pending && 'opacity-60 pointer-events-none'
    )}
  >
    <Checkbox
      checked={checked}
      onCheckedChange={() => onChange()}
      className="h-5 w-5 shrink-0"
      aria-label={label}
      disabled={inventoryBadge}
    />
    <span className="select-none">{label}</span>
    {lockedPriceAnnotation && (
      <span className="text-[10px] tabular-nums text-muted-foreground">
        @ {lockedPriceAnnotation}
      </span>
    )}
    {inventoryBadge && (
      <span className="rounded border border-primary/40 bg-primary/10 px-1 text-[10px] font-medium text-primary">
        in your Inventory
      </span>
    )}
  </label>
)
